'use client';

import { useState } from 'react';
import { Calculator, TrendingUp, Percent } from 'lucide-react';

const ROICalculator = () => {
  const [companySize, setCompanySize] = useState('medium');
  const [emissionLevel, setEmissionLevel] = useState('medium');
  const [calculatedROI, setCalculatedROI] = useState<any>(null);

  const companySizes = {
    small: { label: 'Small (< 500 employees)', multiplier: 1 },
    medium: { label: 'Medium (500-2000 employees)', multiplier: 2.5 },
    large: { label: 'Large (2000-10000 employees)', multiplier: 5 },
    enterprise: { label: 'Enterprise (> 10000 employees)', multiplier: 10 }
  };

  const emissionLevels = {
    low: { label: 'Low (< 1000 tCO2e/year)', factor: 1 },
    medium: { label: 'Medium (1000-10000 tCO2e/year)', factor: 2 },
    high: { label: 'High (10000-100000 tCO2e/year)', factor: 4 },
    veryhigh: { label: 'Very High (> 100000 tCO2e/year)', factor: 8 }
  };

  const calculateROI = () => {
    const sizeMultiplier = companySizes[companySize as keyof typeof companySizes].multiplier;
    const emissionFactor = emissionLevels[emissionLevel as keyof typeof emissionLevels].factor;
    
    const baseRevenue = 5000000; // ₹50L base
    const carbonCreditRevenue = baseRevenue * sizeMultiplier * emissionFactor * 0.8;
    const complianceSavings = baseRevenue * sizeMultiplier * emissionFactor * 0.6;
    const operationalSavings = baseRevenue * sizeMultiplier * emissionFactor * 0.4;
    const marketplaceRevenue = baseRevenue * sizeMultiplier * emissionFactor * 1.2;
    
    const totalRevenue = carbonCreditRevenue + complianceSavings + operationalSavings + marketplaceRevenue;
    const investmentRequired = baseRevenue * sizeMultiplier * 0.3;
    const netReturn = totalRevenue - investmentRequired;
    const roiPercentage = ((netReturn / investmentRequired) * 100).toFixed(0);
    const paybackMonths = Math.ceil((investmentRequired / totalRevenue) * 12);

    setCalculatedROI({
      carbonCreditRevenue,
      complianceSavings,
      operationalSavings,
      marketplaceRevenue,
      totalRevenue,
      investmentRequired,
      netReturn,
      roiPercentage,
      paybackMonths
    });
  };

  const formatCurrency = (amount: number) => {
    if (amount >= 10000000) {
      return `₹${(amount / 10000000).toFixed(2)}Cr`;
    } else if (amount >= 100000) {
      return `₹${(amount / 100000).toFixed(2)}L`;
    }
    return `₹${amount.toLocaleString('en-IN')}`;
  };

  return (
    <section className="py-20 bg-gradient-to-br from-emerald-50 via-white to-teal-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-3 px-6 py-3 bg-emerald-100 rounded-full mb-6">
            <Calculator className="w-5 h-5 text-emerald-600" />
            <span className="text-emerald-900 font-semibold">ROI Calculator</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
            Calculate Your ESG Investment Returns
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Discover potential revenue from carbon credits, compliance savings, and operational efficiency
          </p>
        </div>

        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Input Section */}
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <h3 className="text-2xl font-bold text-slate-900 mb-6">Your Company Profile</h3>
              
              {/* Company Size Selector */}
              <div className="mb-8">
                <label className="block text-sm font-semibold text-slate-700 mb-3">Company Size</label>
                <div className="space-y-3">
                  {Object.entries(companySizes).map(([key, value]) => (
                    <label
                      key={key}
                      className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                        companySize === key
                          ? 'border-emerald-500 bg-emerald-50' :'border-slate-200 hover:border-emerald-300 hover:bg-slate-50'
                      }`}
                    >
                      <input
                        type="radio"
                        name="companySize"
                        value={key}
                        checked={companySize === key}
                        onChange={(e) => setCompanySize(e.target.value)}
                        className="w-5 h-5 text-emerald-600"
                      />
                      <span className="font-medium text-slate-900">{value.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Emission Level Selector */}
              <div className="mb-8">
                <label className="block text-sm font-semibold text-slate-700 mb-3">Annual Emission Level</label>
                <div className="space-y-3">
                  {Object.entries(emissionLevels).map(([key, value]) => (
                    <label
                      key={key}
                      className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                        emissionLevel === key
                          ? 'border-emerald-500 bg-emerald-50' :'border-slate-200 hover:border-emerald-300 hover:bg-slate-50'
                      }`}
                    >
                      <input
                        type="radio"
                        name="emissionLevel"
                        value={key}
                        checked={emissionLevel === key}
                        onChange={(e) => setEmissionLevel(e.target.value)}
                        className="w-5 h-5 text-emerald-600"
                      />
                      <span className="font-medium text-slate-900">{value.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <button
                onClick={calculateROI}
                className="w-full py-4 bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-semibold rounded-xl hover:from-emerald-700 hover:to-teal-700 transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                Calculate ROI
              </button>
            </div>

            {/* Results Section */}
            <div className="bg-gradient-to-br from-slate-900 to-emerald-900 rounded-2xl shadow-xl p-8 text-white">
              <h3 className="text-2xl font-bold mb-6">Projected Returns</h3>
              
              {!calculatedROI ? (
                <div className="flex items-center justify-center h-64">
                  <div className="text-center">
                    <Calculator className="w-16 h-16 mx-auto mb-4 text-white/50" />
                    <p className="text-white/70">Select your parameters and calculate to see results</p>
                  </div>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Revenue Breakdown */}
                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                    <h4 className="font-semibold mb-4 text-emerald-400">Revenue Streams</h4>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-white/80">Carbon Credit Revenue</span>
                        <span className="font-bold">{formatCurrency(calculatedROI.carbonCreditRevenue)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-white/80">Compliance Savings</span>
                        <span className="font-bold">{formatCurrency(calculatedROI.complianceSavings)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-white/80">Operational Efficiency</span>
                        <span className="font-bold">{formatCurrency(calculatedROI.operationalSavings)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-white/80">Marketplace Revenue</span>
                        <span className="font-bold">{formatCurrency(calculatedROI.marketplaceRevenue)}</span>
                      </div>
                      <div className="pt-3 border-t border-white/20 flex justify-between">
                        <span className="font-bold text-emerald-400">Total Revenue</span>
                        <span className="font-bold text-2xl text-emerald-400">
                          {formatCurrency(calculatedROI.totalRevenue)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Key Metrics */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Percent className="w-5 h-5 text-emerald-400" />
                        <span className="text-sm text-white/70">ROI</span>
                      </div>
                      <p className="text-3xl font-bold text-emerald-400">{calculatedROI.roiPercentage}%</p>
                    </div>
                    <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <TrendingUp className="w-5 h-5 text-emerald-400" />
                        <span className="text-sm text-white/70">Payback</span>
                      </div>
                      <p className="text-3xl font-bold text-emerald-400">{calculatedROI.paybackMonths}mo</p>
                    </div>
                  </div>

                  {/* Investment vs Return */}
                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                    <div className="flex justify-between mb-2">
                      <span className="text-white/80">Investment Required</span>
                      <span className="font-bold text-red-400">{formatCurrency(calculatedROI.investmentRequired)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white/80">Net Return</span>
                      <span className="font-bold text-emerald-400">{formatCurrency(calculatedROI.netReturn)}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ROICalculator;